/**
 * Section B7.4 Page - B.7.4.減量移除計算
 * UI 模式：DataTable + 精靈新增（3步驟）/ 3頁籤編輯
 * 頁面底部提供「年度預估減量試算」區塊，依 Section C 產生 XLSX 供下載。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import LatexHelper from '../components/LatexHelper';
import sectionB74Service, {
  SectionB74Detail, EquationEntry, MethodChoiceItem,
} from '../services/sectionB74Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import '../styles/DataTable.css';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import { parseLatexSymbols, normalizeSubscripts } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';

// ── 數值格式化 ────────────────────────────────────────────────────
function fmtNum(v: number | null | undefined, d = 2): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
}

// ── LaTeX 自動計算 ────────────────────────────────────────────────
function evaluateLatex(latex: string, params: MethodChoiceItem[]): number | null {
  try {
    let expr = latex.includes('=')
      ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
      : latex;
    // 正規化頂層單字元下標（OX_y → OX_{y}）
    { let r = '', d = 0, i = 0; while (i < expr.length) { const c = expr[i]; if (c === '\\') { r += c + (expr[i+1]??''); i+=2; } else if (c === '{') { d++; r+=c; i++; } else if (c === '}') { d--; r+=c; i++; } else if (d===0 && c==='_' && i+1<expr.length && /[A-Za-z0-9]/.test(expr[i+1])) { r+='_{'+expr[i+1]+'}'; i+=2; } else { r+=c; i++; } } expr=r; }
    const filled = [...params]
      .filter(p => p.annual_value !== null && p.symbol.trim())
      .sort((a, b) => b.symbol.length - a.symbol.length);
    for (const p of filled) {
      const sym = normalizeSubscripts(p.symbol);
      const escaped = sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(new RegExp(escaped, 'g'), `(${p.annual_value})`);
    }
    expr = expr
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '')
      .replace(/\\times/g, '*').replace(/\\div/g, '/').replace(/\\cdot/g, '*')
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '(').replace(/\\right\s*\]/g, ')')
      .replace(/\[/g, '(').replace(/\]/g, ')')
      .replace(/\\left\s*\\{/g, '(').replace(/\\right\s*\\}/g, ')')
      .replace(/\^\{([^}]*)\}/g, '**($1)').replace(/\^([0-9]+)/g, '**$1')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/\{/g, '(').replace(/\}/g, ')')
      .replace(/_/g, '');
    if (/[^0-9+\-*/().%\s,MathsqrtpowPIE]/.test(expr.replace(/Math\.[a-z]+/g, ''))) return null;
    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;
  } catch { return null; }
}

// ── 常用單位 ──────────────────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e', latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e', latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh', latex: '\\text{MWh}' }, { label: 'GWh', latex: '\\text{GWh}' },
  { label: 'kWh', latex: '\\text{kWh}' }, { label: 'GJ', latex: '\\text{GJ}' },
  { label: 'MJ', latex: '\\text{MJ}' }, { label: 'kg', latex: '\\text{kg}' },
  { label: 't', latex: '\\text{t}' }, { label: 'm³', latex: '\\text{m}^3' },
  { label: 'Nm³', latex: '\\text{Nm}^3' }, { label: 'L', latex: '\\text{L}' },
  { label: '%', latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];

// ── UnitInput ─────────────────────────────────────────────────────
interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          placeholder="LaTeX，如 \text{tCO}_2\text{e}"
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }} />
        {value.trim() && (
          <div style={{ minWidth: 50, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {preview()}
          </div>
        )}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa', color: showPicker ? '#fff' : '#555', cursor: 'pointer' }}>≡</button>
        )}
      </div>
      {showPicker && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 300, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 300 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button" onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer' }}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ════════════════════════════════════════════════════════════════
// Section Props 共用介面
// ════════════════════════════════════════════════════════════════
interface SectionProps {
  entry: EquationEntry;
  projectId: number;
  sectionId: number;
  canEdit: boolean;
  isZh: boolean;
  onRefresh: (d: SectionB74Detail) => void;
  onNavigate?: (tab: number) => void;
}

// ════════════════════════════════════════════════════════════════
// Section 1：計算公式
// ════════════════════════════════════════════════════════════════
interface Section1Props extends SectionProps { equationFromCodes: SystemCode[]; }

const Section1Formula: React.FC<Section1Props> = ({ entry, projectId, canEdit, isZh, onRefresh, onNavigate, equationFromCodes }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [equationFrom, setEquationFrom] = useState<number | null>(entry.equation_from ?? null);
  const [name, setName] = useState(entry.name);
  const [equation, setEquation] = useState(entry.equation);
  const [note, setNote] = useState(entry.equation_note);
  const [latexRef, setLatexRef] = useState<HTMLTextAreaElement | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEquationFrom(entry.equation_from ?? null);
    setName(entry.name);
    setEquation(entry.equation);
    setNote(entry.equation_note);
  }, [entry]);

  const getNameLabel = () => {
    if (!equationFrom) return isZh ? '名稱' : 'Name';
    const found = equationFromCodes.find(c => c.id === equationFrom);
    if (!found) return isZh ? '名稱' : 'Name';
    return isZh ? `${found.code_cname}名稱` : `${found.code_ename} name`;
  };

  const insertLatex = (snippet: string) => {
    if (!latexRef) { setEquation(v => v + snippet); return; }
    const s = latexRef.selectionStart ?? latexRef.value.length;
    const e = latexRef.selectionEnd ?? latexRef.value.length;
    const next = latexRef.value.slice(0, s) + snippet + latexRef.value.slice(e);
    setEquation(next);
    setTimeout(() => { latexRef.focus(); latexRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB74Service.updateEquation(projectId, entry.id, {
        equation_from: equationFrom, name, equation, equation_note: note,
      });
      onRefresh(res); show(true, t('sectionB74.saveSuccess'));
    } catch { show(false, t('sectionB74.saveFailed')); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: '0 0 200px' }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
            {isZh ? '方程式來源類別' : 'Source type'}
          </label>
          <select value={equationFrom ?? ''} onChange={e => setEquationFrom(e.target.value ? Number(e.target.value) : null)}
            disabled={!canEdit}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}>
            <option value="">{isZh ? '— 請選擇 —' : '— Select —'}</option>
            {equationFromCodes.map(code => (
              <option key={code.id} value={code.id}>
                {isZh ? code.code_cname : code.code_ename}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
            {getNameLabel()}
          </label>
          <input value={name} onChange={e => setName(e.target.value)} disabled={!canEdit}
            placeholder={isZh ? '如：AM0096、CDM Tool 03' : 'e.g., AM0096, CDM Tool 03'}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB74.equationLabel')}</label>
          <LatexHelper isZh={isZh} onInsert={insertLatex} />
        </div>
        <textarea ref={setLatexRef} value={equation} onChange={e => setEquation(e.target.value)} disabled={!canEdit}
          rows={3} placeholder="ER_{y} = BE_{y} - PE_{y} - LE_{y}"
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
        {equation.trim() && (
          <div style={{ marginTop: 6, padding: '8px 12px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, overflowX: 'auto' }}>
            <BlockMath math={equation} />
          </div>
        )}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
          {t('sectionB74.noteLabel')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB74.optional')}, ex ante / ex post)</span>
        </label>
        <RichTextEditor value={note} onChange={setNote} disabled={!canEdit} minHeight="72px" />
      </div>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB74.saving') : t('sectionB74.save')}
          </button>
          {onNavigate && (
            <button onClick={() => onNavigate(1)}
              style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #4A90E2', background: '#fff', color: '#4A90E2', cursor: 'pointer', fontWeight: 600 }}>
              {isZh ? '計算參數說明 →' : 'Parameter Description →'}
            </button>
          )}
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 2：方法論說明（參數）
// ════════════════════════════════════════════════════════════════
interface Section2Props extends SectionProps { paraTypeCodes: SystemCode[]; }

const Section2MethodChoices: React.FC<Section2Props> = ({ entry, projectId, canEdit, isZh, onRefresh, onNavigate, paraTypeCodes }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [choices, setChoices] = useState<MethodChoiceItem[]>(entry.method_choices || []);
  const [saving, setSaving] = useState(false);
  const [formulaConfigs, setFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [formulaId, setFormulaId] = useState<number | ''>('');

  useEffect(() => { formulaConfigService.listActive().then(setFormulaConfigs).catch(() => {}); }, []);

  useEffect(() => {
    const parsed = parseLatexSymbols(entry.equation);
    if (!entry.method_choices?.length && parsed.length) {
      setChoices(parsed.map(sym => ({ symbol: sym, para_type: null, scenarios: '', unit: '' })));
    } else {
      setChoices(entry.method_choices || []);
    }
  }, [entry]);

  const applyFormulaConfig = (id: number | '') => {
    setFormulaId(id);
    if (!id) return;
    const fc = formulaConfigs.find(f => f.id === id);
    if (!fc) return;
    const parsed = parseLatexSymbols(entry.equation);
    const newChoices: MethodChoiceItem[] = parsed.map(sym => {
      const cp = fc.parameters.find(p => p.symbol === sym);
      return { symbol: sym, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '' };
    });
    setChoices(newChoices);
  };

  const update = (idx: number, field: keyof MethodChoiceItem, value: any) =>
    setChoices(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  const addRow = () => setChoices(prev => [...prev, { symbol: '', para_type: null, scenarios: '', unit: '' }]);
  const removeRow = (idx: number) => setChoices(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB74Service.updateEquation(projectId, entry.id, { method_choices: choices });
      onRefresh(res); show(true, t('sectionB74.saveSuccess'));
    } catch { show(false, t('sectionB74.saveFailed')); } finally { setSaving(false); }
  };

  const getTypeLabel = (code: number | null | undefined) => {
    if (!code) return t('sectionB74.allTypes');
    const found = paraTypeCodes.find(c => c.id === code);
    return found ? (isZh ? found.code_cname : found.code_ename) : String(code);
  };

  return (
    <div>
      {/* 從公式庫帶入參數說明 */}
      {canEdit && formulaConfigs.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{isZh ? '從公式庫帶入：' : 'From library:'}</label>
          <select value={formulaId} onChange={e => applyFormulaConfig(e.target.value ? Number(e.target.value) : '')}
            style={{ flex: 1, padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}>
            <option value="">-- {isZh ? '選擇套用（將覆蓋現有說明）' : 'Select to apply (overwrites descriptions)'} --</option>
            {formulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
          </select>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
        <thead className="table-header-dark-green">
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colSymbol')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 130 }}>{t('sectionB74.colParaType')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB74.colScenarios')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 160 }}>{t('sectionB74.colUnit')}</th>
            {canEdit && <th style={{ width: 30 }}></th>}
          </tr>
        </thead>
        <tbody>
          {choices.map((c, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '5px 8px' }}>
                <input value={c.symbol} onChange={e => update(i, 'symbol', e.target.value)} disabled={!canEdit}
                  style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }} />
              </td>
              <td style={{ padding: '5px 8px' }}>
                {canEdit ? (
                  <select value={c.para_type ?? ''} onChange={e => update(i, 'para_type', e.target.value ? Number(e.target.value) : null)}
                    style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12 }}>
                    <option value="">{t('sectionB74.allTypes')}</option>
                    {paraTypeCodes.map(code => (
                      <option key={code.id} value={code.id}>{isZh ? code.code_cname : code.code_ename}</option>
                    ))}
                  </select>
                ) : <span style={{ fontSize: 11, color: '#666' }}>{getTypeLabel(c.para_type)}</span>}
              </td>
              <td style={{ padding: '5px 8px', minWidth: 200 }}>
                <RichTextEditor value={c.scenarios} onChange={html => update(i, 'scenarios', html)}
                  disabled={!canEdit} minHeight="60px" toolbar="inline" />
              </td>
              <td style={{ padding: '5px 8px' }}>
                <UnitInput value={c.unit} onChange={v => update(i, 'unit', v)} disabled={!canEdit} />
              </td>
              {canEdit && (
                <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                  <button onClick={() => removeRow(i)} style={{ padding: '2px 6px', fontSize: 11, border: '1px solid #e53935', borderRadius: 3, color: '#e53935', background: '#fff', cursor: 'pointer' }}>✕</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={addRow} style={{ padding: '5px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', color: '#4A90E2', background: '#fff', cursor: 'pointer' }}>
            + {t('sectionB74.addParameter')}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '5px 16px', fontSize: 12, borderRadius: 5, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB74.saving') : t('sectionB74.save')}
          </button>
          {onNavigate && (<>
            <button onClick={() => onNavigate(0)}
              style={{ padding: '5px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #aaa', background: '#fff', color: '#555', cursor: 'pointer' }}>
              ← {isZh ? '方程式說明' : 'Equations'}
            </button>
            <button onClick={() => onNavigate(2)}
              style={{ padding: '5px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#fff', color: '#4A90E2', cursor: 'pointer', fontWeight: 600 }}>
              {isZh ? '計算年度預估值 →' : 'Estimated Value →'}
            </button>
          </>)}
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 3：保守評估說明
// ════════════════════════════════════════════════════════════════
const Section3Conservative: React.FC<SectionProps> = ({ entry, projectId, canEdit, isZh, onRefresh, onNavigate }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [choices, setChoices] = useState<MethodChoiceItem[]>([]);
  const [calcResult, setCalcResult] = useState(entry.calculation_result);
  const [conservativeDesc, setConservativeDesc] = useState(entry.conservative_description || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChoices((entry.method_choices || []).map(c => ({ ...c })));
    setCalcResult({ ...entry.calculation_result });
    setConservativeDesc(entry.conservative_description || '');
  }, [entry]);

  const updateValue = (idx: number, val: string) =>
    setChoices(prev => prev.map((c, i) => i === idx ? { ...c, annual_value: val === '' ? null : parseFloat(val) } : c));

  const handleCalc = () => {
    const result = evaluateLatex(entry.equation, choices);
    if (result !== null) {
      setCalcResult(prev => ({ ...prev, value: result }));
    } else {
      const manual = window.prompt(t('sectionB74.calcAutoError'));
      if (manual !== null) setCalcResult(prev => ({ ...prev, value: parseFloat(manual) || null }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB74Service.updateEquation(projectId, entry.id, {
        method_choices: choices, calculation_result: calcResult, conservative_description: conservativeDesc,
      });
      onRefresh(res); show(true, t('sectionB74.saveSuccess'));
    } catch { show(false, t('sectionB74.saveFailed')); } finally { setSaving(false); }
  };

  if (!choices.length) return <div style={{ color: '#999', fontSize: 13 }}>{t('sectionB74.noParamsYet')}</div>;

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
        <thead className="table-header-dark-green">
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colSymbol')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB74.colScenarios')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colUnit')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'right', width: 130 }}>{t('sectionB74.colAnnualValue')}</th>
          </tr>
        </thead>
        <tbody>
          {choices.map((c, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '5px 8px' }}>
                <code style={{ fontSize: 11, background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{c.symbol}</code>
              </td>
              <td style={{ padding: '5px 8px', fontSize: 11, color: '#666' }}>
                {c.scenarios ? <span dangerouslySetInnerHTML={{ __html: c.scenarios }} /> : '—'}
              </td>
              <td style={{ padding: '5px 8px', fontSize: 11 }}>{c.unit ? <InlineMath math={c.unit} /> : '—'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                <input type="number" value={c.annual_value ?? ''} onChange={e => updateValue(i, e.target.value)}
                  disabled={!canEdit}
                  style={{ width: 110, padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {canEdit && (
          <button onClick={handleCalc}
            style={{ padding: '6px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer' }}>
            ▶ {t('sectionB74.calculate')}
          </button>
        )}
        <span style={{ fontSize: 13, fontWeight: 500 }}>{t('sectionB74.calcResult')}</span>
        <span style={{ minWidth: 120, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 5, fontSize: 13, textAlign: 'right', background: '#f5f5f5', display: 'inline-block' }}>
          {calcResult.value != null ? fmtNum(calcResult.value) : '—'}
        </span>
        <span style={{ fontSize: 12, color: '#888' }}>{t('sectionB74.calcUnit')}</span>
        <div style={{ width: 200 }}>
          <UnitInput value={calcResult.unit} onChange={v => setCalcResult(prev => ({ ...prev, unit: v }))} disabled={!canEdit} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB74.conservativeLabel')}</label>
        <RichTextEditor value={conservativeDesc} onChange={setConservativeDesc} disabled={!canEdit} />
      </div>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {onNavigate && (
            <button onClick={() => onNavigate(1)}
              style={{ padding: '7px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #aaa', background: '#fff', color: '#555', cursor: 'pointer' }}>
              ← {isZh ? '計算參數說明' : 'Parameter Description'}
            </button>
          )}
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB74.saving') : t('sectionB74.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// EquationModal：新增/編輯方程式 Modal（精靈新增 / 3 頁籤編輯）
// ════════════════════════════════════════════════════════════════
interface EquationModalProps {
  projectId: number;
  sectionId: number;
  initialEntry: EquationEntry | null;
  isZh: boolean;
  canEdit: boolean;
  paraTypeCodes: SystemCode[];
  equationFromCodes: SystemCode[];
  onClose: () => void;
  onDataChange: (d: SectionB74Detail) => void;
}

const EquationModal: React.FC<EquationModalProps> = ({
  projectId, sectionId, initialEntry, isZh, canEdit, paraTypeCodes, equationFromCodes, onClose, onDataChange,
}) => {
  const { t } = useTranslation();
  const isNew = initialEntry === null;

  // ── 精靈模式（新增）共用狀態 ───────────────────────────────────
  const [wizStep, setWizStep] = useState(0); // 0=方程式說明 1=計算參數說明 2=計算年度預估值
  // Step 1
  const [equationFrom, setEquationFrom] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [equation, setEquation] = useState('');
  const [note, setNote] = useState('');
  const [latexRef, setLatexRef] = useState<HTMLTextAreaElement | null>(null);
  // Step 2
  const [wizChoices, setWizChoices] = useState<MethodChoiceItem[]>([]);
  // Step 3
  const [wizCalcResult, setWizCalcResult] = useState<{ value: number | null; unit: string }>({ value: null, unit: '' });
  const [wizConservativeDesc, setWizConservativeDesc] = useState('');
  const [saving, setSaving] = useState(false);

  // ── 編輯模式 ────────────────────────────────────────────────────
  const [entry, setEntry] = useState<EquationEntry | null>(initialEntry);
  const [activeTab, setActiveTab] = useState(0);

  const TABS = [
    isZh ? '① 方程式說明' : '① Equations',
    isZh ? '② 計算參數說明' : '② Parameter Description',
    isZh ? '③ 計算年度預估值' : '③ Estimated Value',
  ];

  const WIZ_STEPS = [
    isZh ? '方程式說明' : 'Equations',
    isZh ? '計算參數說明' : 'Parameter Description',
    isZh ? '計算年度預估值' : 'Estimated Value',
  ];

  const getNameLabel = () => {
    if (!equationFrom) return isZh ? '名稱' : 'Name';
    const found = equationFromCodes.find(c => c.id === equationFrom);
    if (!found) return isZh ? '名稱' : 'Name';
    return isZh ? `${found.code_cname}名稱` : `${found.code_ename} name`;
  };

  const handleRefresh = (d: SectionB74Detail) => {
    onDataChange(d);
    if (entry) {
      const updated = (d.equations ?? []).find(e => e.id === entry.id);
      if (updated) setEntry(updated);
    }
  };

  const insertLatex = (snippet: string) => {
    if (!latexRef) { setEquation(v => v + snippet); return; }
    const s = latexRef.selectionStart ?? latexRef.value.length;
    const e2 = latexRef.selectionEnd ?? latexRef.value.length;
    setEquation(latexRef.value.slice(0, s) + snippet + latexRef.value.slice(e2));
    setTimeout(() => { latexRef.focus(); latexRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  // Step 0 → Step 1：自動解析公式參數
  const goToStep1 = () => {
    if (!wizChoices.length && equation.trim()) {
      const parsed = parseLatexSymbols(equation);
      setWizChoices(parsed.map(sym => ({ symbol: sym, para_type: null, scenarios: '', unit: '' })));
    }
    setWizStep(1);
  };

  // Step 2 最終儲存：addEquation + updateEquation 一次完成
  const handleWizardSave = async () => {
    if (!equation.trim() && !name.trim()) return;
    setSaving(true);
    try {
      const res1 = await sectionB74Service.addEquation(projectId, {
        equation_from: equationFrom, name, equation, equation_note: note,
      });
      onDataChange(res1);
      const newEntry = (res1.equations ?? []).at(-1);
      if (!newEntry) return;

      const res2 = await sectionB74Service.updateEquation(projectId, newEntry.id, {
        method_choices: wizChoices,
        calculation_result: wizCalcResult,
        conservative_description: wizConservativeDesc || null,
      });
      onDataChange(res2);

      // 精靈 3 步驟全部完成，直接關閉 Modal
      onClose();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  // 精靈模式：計算功能（Step 2）
  const handleWizCalc = () => {
    const result = evaluateLatex(equation, wizChoices);
    if (result !== null) {
      setWizCalcResult(prev => ({ ...prev, value: result }));
    } else {
      const manual = window.prompt(t('sectionB74.calcAutoError'));
      if (manual !== null) setWizCalcResult(prev => ({ ...prev, value: parseFloat(manual) || null }));
    }
  };

  // ── 精靈步驟指示器 ─────────────────────────────────────────────
  const WizStepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '10px 24px', borderBottom: '1px solid #e8e8e8', flexShrink: 0, background: '#fafcff' }}>
      {WIZ_STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
              background: i < wizStep ? '#52c41a' : i === wizStep ? '#4A90E2' : '#e0e0e0',
              color: i <= wizStep ? '#fff' : '#999',
            }}>
              {i < wizStep ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 12, fontWeight: i === wizStep ? 700 : 400, color: i === wizStep ? '#4A90E2' : i < wizStep ? '#52c41a' : '#999' }}>
              {label}
            </span>
          </div>
          {i < WIZ_STEPS.length - 1 && (
            <div style={{ flex: 1, height: 1, minWidth: 24, background: i < wizStep ? '#52c41a' : '#e0e0e0', margin: '0 8px' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: 1200, height: '88vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
      }}>
        {/* 標題列 */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {isNew && !entry
              ? t('sectionB74.newEquation')
              : `${t('sectionB74.editEquation')} — ${entry?.name || entry?.equation?.slice(0, 30) || ''}`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
        </div>

        {/* 新增模式：精靈 3 步驟 */}
        {isNew && !entry ? (
          <>
            <WizStepIndicator />
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>

              {/* ── Step 0：方程式說明 ── */}
              {wizStep === 0 && (<>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: '0 0 200px' }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      {isZh ? '方程式來源類別' : 'Source type'}
                    </label>
                    <select value={equationFrom ?? ''} onChange={e => setEquationFrom(e.target.value ? Number(e.target.value) : null)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}>
                      <option value="">{isZh ? '— 請選擇 —' : '— Select —'}</option>
                      {equationFromCodes.map(code => (
                        <option key={code.id} value={code.id}>{isZh ? code.code_cname : code.code_ename}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{getNameLabel()}</label>
                    <input value={name} onChange={e => setName(e.target.value)}
                      placeholder={isZh ? '如：AM0096、CDM Tool 03' : 'e.g., AM0096, CDM Tool 03'}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB74.equationLabel')}</label>
                    <LatexHelper isZh={isZh} onInsert={insertLatex} />
                  </div>
                  <textarea ref={setLatexRef} value={equation} onChange={e => setEquation(e.target.value)}
                    rows={3} placeholder="ER_{y} = BE_{y} - PE_{y} - LE_{y}"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
                  {equation.trim() && (
                    <div style={{ marginTop: 6, padding: '8px 12px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, overflowX: 'auto' }}>
                      <BlockMath math={equation} />
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    {t('sectionB74.noteLabel')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB74.optional')})</span>
                  </label>
                  <RichTextEditor value={note} onChange={setNote} minHeight="60px" />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={goToStep1} disabled={!equation.trim() && !name.trim()}
                    style={{ padding: '8px 22px', fontSize: 13, borderRadius: 6, border: 'none', background: (!equation.trim() && !name.trim()) ? '#ccc' : '#4A90E2', color: '#fff', cursor: (!equation.trim() && !name.trim()) ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {isZh ? '計算參數說明 →' : 'Parameter Description →'}
                  </button>
                  <button onClick={onClose}
                    style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                    {t('common.cancel')}
                  </button>
                </div>
              </>)}

              {/* ── Step 1：計算參數說明 ── */}
              {wizStep === 1 && (<>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                  <thead className="table-header-dark-green">
                    <tr>
                      <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colSymbol')}</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left', width: 130 }}>{t('sectionB74.colParaType')}</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB74.colScenarios')}</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left', width: 160 }}>{t('sectionB74.colUnit')}</th>
                      <th style={{ width: 30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {wizChoices.map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '5px 8px' }}>
                          <input value={c.symbol} onChange={e => setWizChoices(prev => prev.map((x, j) => j === i ? { ...x, symbol: e.target.value } : x))}
                            style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }} />
                        </td>
                        <td style={{ padding: '5px 8px' }}>
                          <select value={c.para_type ?? ''} onChange={e => setWizChoices(prev => prev.map((x, j) => j === i ? { ...x, para_type: e.target.value ? Number(e.target.value) : null } : x))}
                            style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12 }}>
                            <option value="">{t('sectionB74.allTypes')}</option>
                            {paraTypeCodes.map(code => (
                              <option key={code.id} value={code.id}>{isZh ? code.code_cname : code.code_ename}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '5px 8px', minWidth: 200 }}>
                          <RichTextEditor value={c.scenarios} onChange={html => setWizChoices(prev => prev.map((x, j) => j === i ? { ...x, scenarios: html } : x))}
                            minHeight="60px" toolbar="inline" />
                        </td>
                        <td style={{ padding: '5px 8px' }}>
                          <UnitInput value={c.unit} onChange={v => setWizChoices(prev => prev.map((x, j) => j === i ? { ...x, unit: v } : x))} />
                        </td>
                        <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                          <button onClick={() => setWizChoices(prev => prev.filter((_, j) => j !== i))}
                            style={{ padding: '2px 6px', fontSize: 11, border: '1px solid #e53935', borderRadius: 3, color: '#e53935', background: '#fff', cursor: 'pointer' }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => setWizChoices(prev => [...prev, { symbol: '', para_type: null, scenarios: '', unit: '' }])}
                    style={{ padding: '5px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', color: '#4A90E2', background: '#fff', cursor: 'pointer' }}>
                    + {t('sectionB74.addParameter')}
                  </button>
                  <button onClick={() => setWizStep(0)}
                    style={{ padding: '5px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #aaa', background: '#fff', color: '#555', cursor: 'pointer' }}>
                    ← {isZh ? '方程式說明' : 'Equations'}
                  </button>
                  <button onClick={() => setWizStep(2)}
                    style={{ padding: '5px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#fff', color: '#4A90E2', cursor: 'pointer', fontWeight: 600 }}>
                    {isZh ? '計算年度預估值 →' : 'Estimated Value →'}
                  </button>
                </div>
              </>)}

              {/* ── Step 2：計算年度預估值 ── */}
              {wizStep === 2 && (<>
                {wizChoices.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13, marginBottom: 14 }}>{t('sectionB74.noParamsYet')}</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
                    <thead className="table-header-dark-green">
                      <tr>
                        <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colSymbol')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB74.colScenarios')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB74.colUnit')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right', width: 130 }}>{t('sectionB74.colAnnualValue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wizChoices.map((c, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '5px 8px' }}>
                            <code style={{ fontSize: 11, background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{c.symbol}</code>
                          </td>
                          <td style={{ padding: '5px 8px', fontSize: 11, color: '#666' }}>
                            {c.scenarios ? <span dangerouslySetInnerHTML={{ __html: c.scenarios }} /> : '—'}
                          </td>
                          <td style={{ padding: '5px 8px', fontSize: 11 }}>{c.unit ? <InlineMath math={c.unit} /> : '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                            <input type="number" value={c.annual_value ?? ''} onChange={e => setWizChoices(prev => prev.map((x, j) => j === i ? { ...x, annual_value: e.target.value === '' ? null : parseFloat(e.target.value) } : x))}
                              style={{ width: 110, padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <button onClick={handleWizCalc}
                    style={{ padding: '6px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer' }}>
                    ▶ {t('sectionB74.calculate')}
                  </button>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{t('sectionB74.calcResult')}</span>
                  <span style={{ minWidth: 120, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 5, fontSize: 13, textAlign: 'right', background: '#f5f5f5', display: 'inline-block' }}>
                    {wizCalcResult.value != null ? fmtNum(wizCalcResult.value) : '—'}
                  </span>
                  <span style={{ fontSize: 12, color: '#888' }}>{t('sectionB74.calcUnit')}</span>
                  <div style={{ width: 200 }}>
                    <UnitInput value={wizCalcResult.unit} onChange={v => setWizCalcResult(prev => ({ ...prev, unit: v }))} />
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB74.conservativeLabel')}</label>
                  <RichTextEditor value={wizConservativeDesc} onChange={setWizConservativeDesc} />
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => setWizStep(1)}
                    style={{ padding: '7px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #aaa', background: '#fff', color: '#555', cursor: 'pointer' }}>
                    ← {isZh ? '計算參數說明' : 'Parameter Description'}
                  </button>
                  <button onClick={handleWizardSave} disabled={saving}
                    style={{ padding: '7px 22px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {saving ? t('sectionB74.saving') : t('sectionB74.save')}
                  </button>
                  <button onClick={onClose}
                    style={{ padding: '7px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                    {t('common.cancel')}
                  </button>
                </div>
              </>)}

            </div>
          </>
        ) : entry ? (
          <>
            <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', flexShrink: 0, overflowX: 'auto' }}>
              {TABS.map((label, i) => (
                <button key={i} onClick={() => setActiveTab(i)}
                  style={{
                    padding: '10px 16px', fontSize: 12, border: 'none',
                    borderBottom: activeTab === i ? '2px solid #4A90E2' : '2px solid transparent',
                    background: 'none', color: activeTab === i ? '#4A90E2' : '#555',
                    cursor: 'pointer', fontWeight: activeTab === i ? 600 : 400, whiteSpace: 'nowrap',
                  }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              {activeTab === 0 && <Section1Formula entry={entry} projectId={projectId} sectionId={sectionId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} onNavigate={setActiveTab} equationFromCodes={equationFromCodes} />}
              {activeTab === 1 && <Section2MethodChoices entry={entry} projectId={projectId} sectionId={sectionId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} onNavigate={setActiveTab} paraTypeCodes={paraTypeCodes} />}
              {activeTab === 2 && <Section3Conservative entry={entry} projectId={projectId} sectionId={sectionId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} onNavigate={setActiveTab} />}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SpreadsheetSection：年度預估減量試算區塊
// ════════════════════════════════════════════════════════════════
interface SpreadsheetSectionProps {
  projectId: number;
  sectionCComplete: boolean;
  creditingPeriodDuration: number | null;
  isZh: boolean;
}

const SpreadsheetSection: React.FC<SpreadsheetSectionProps> = ({
  projectId, sectionCComplete, creditingPeriodDuration, isZh,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      await sectionB74Service.downloadSpreadsheetTemplate(projectId);
    } catch {
      setError(isZh ? '下載失敗，請稍後再試' : 'Download failed, please try again');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ marginTop: 32, borderTop: '2px solid #e8e8e8', paddingTop: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#117864', marginBottom: 8 }}>
        {isZh ? '年度預估減量試算 [Annual Emission Reduction Estimate]' : 'Annual Emission Reduction Estimate'}
      </div>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 16, lineHeight: 1.7 }}>
        {isZh
          ? '依 Section C 計入期間資料產生年度試算 XLSX 範本，填入 B.7.1/B.7.2/B.7.3 各節估算數值後，可至 B.7.6 上傳匯入，省去逐年手動輸入的步驟。'
          : 'Generate an annual emission reduction XLSX template based on Section C crediting period. Fill in the estimated values from B.7.1/B.7.2/B.7.3, then upload to B.7.6 for automatic import.'}
      </div>

      {!sectionCComplete ? (
        <div style={{ padding: '16px 20px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, maxWidth: 560 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#d46b08', marginBottom: 4 }}>
            ⚠ {isZh ? '請先完成 C.計入期說明' : 'Please complete Section C first'}
          </div>
          <div style={{ fontSize: 12, color: '#555' }}>
            {isZh
              ? '完成 Section C 後，系統將依計入期間年數自動產生範本列數。'
              : 'After completing Section C, the template rows will be generated based on crediting period duration.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button onClick={handleDownload} disabled={downloading}
            style={{
              padding: '9px 22px', fontSize: 13, borderRadius: 6,
              border: '1px solid #117864', background: downloading ? '#ccc' : '#117864',
              color: '#fff', cursor: downloading ? 'not-allowed' : 'pointer', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            📥 {downloading
              ? (isZh ? '產生中...' : 'Generating...')
              : (isZh ? '下載試算範本 [Download Template]' : 'Download Template')}
          </button>
          {creditingPeriodDuration && (
            <span style={{ fontSize: 12, color: '#888' }}>
              {isZh ? `共 ${creditingPeriodDuration} 個年度列` : `${creditingPeriodDuration} year rows`}
            </span>
          )}
          {error && <span style={{ fontSize: 12, color: '#e53935' }}>{error}</span>}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: '#999', lineHeight: 1.6 }}>
        {isZh
          ? '💡 下載後填入各年度數值，排放減量欄位已含 Excel 自動計算公式。完成後至 B.7.6 頁面使用【上傳試算表】功能匯入。'
          : '💡 Fill in the annual values after downloading. Emission reductions column includes auto-calculation formulas. Then go to B.7.6 to upload and import.'}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// 主元件 SectionB74Page
// ════════════════════════════════════════════════════════════════
const SectionB74Page: React.FC = () => {
  const { i18n, t } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const toast = useToast();

  const [data, setData] = useState<SectionB74Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  const [equationFromCodes, setEquationFromCodes] = useState<SystemCode[]>([]);
  const [modalEntry, setModalEntry] = useState<EquationEntry | null | undefined>(undefined);
  const modalOpen = modalEntry !== undefined;

  const canEdit = hasPermission('section_b7_4', 'update');

  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionB74Page');
      const [result] = await Promise.all([
        sectionB74Service.getSectionB74ByProject(projectId),
        getSystemCodes({ code_etype: 'equation parameters type', is_active: true }).then(setParaTypeCodes),
        getSystemCodes({ code_etype: 'equation from type', is_active: true }).then(setEquationFromCodes),
      ]);
      setData(result);
    } catch (err) { console.error('載入失敗', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setData(null); setModalEntry(undefined); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  const openAdd = () => setModalEntry(null);
  const openEdit = (entry: EquationEntry) => setModalEntry(entry);
  const closeModal = () => setModalEntry(undefined);
  const handleDataChange = (d: SectionB74Detail) => setData(d);

  const handleDelete = async (id: string) => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('sectionB74.deleteConfirm'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await sectionB74Service.deleteEquation(selectedProjectId, id);
      const res = await sectionB74Service.getSectionB74ByProject(selectedProjectId);
      setData(res);
    } catch { toast.error(t('sectionB74.deleteFailed')); }
  };

  const handleReset = async () => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '操作確認' : 'Confirmation',
      message: t('sectionB74.resetConfirm'),
      confirmText: isZh ? '重設' : 'Reset',
    });
    if (!ok) return;
    try {
      await sectionB74Service.resetSectionB74(selectedProjectId);
      setData(null); setModalEntry(undefined);
    } catch (err) { console.error(err); }
  };

  // equation_from 顯示標籤
  const getEquationFromLabel = (code: number | null | undefined) => {
    if (!code) return '—';
    const found = equationFromCodes.find(c => c.id === code);
    return found ? (isZh ? found.code_cname : found.code_ename) : String(code);
  };

  const equations = data?.equations ?? [];

  if (permissionLoading) return <div className="page-container"><div className="loading">Loading...</div></div>;
  if (!hasPermission('section_b7_4', 'read')) return <div className="page-container"><div className="error-message">{t('sectionB74.noPermission')}</div></div>;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_4" />
        {canEdit && data && selectedProjectId && (
          <div className="page-header-right">
            <button onClick={handleReset}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              title={t('sectionB74.resetTitle')}>
              🗑 {t('sectionB74.resetData')}
            </button>
          </div>
        )}
      </div>

      {/* 未選專案 */}
      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB74.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB74.loading')}</div>
      ) : (
        <div>
          {/* 新增按鈕 */}
          {canEdit && (
            <div style={{ marginBottom: 16 }}>
              <button onClick={openAdd}
                style={{ padding: '8px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                + {t('sectionB74.addEquation')}
              </button>
            </div>
          )}

          {/* DataTable */}
          {equations.length > 0 ? (
            <div className="table-container">
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead className="table-header-dark-green">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th style={{ width: 110 }}>{isZh ? '來源' : 'Source'}</th>
                    <th>{isZh ? '名稱' : 'Name'}</th>
                    <th>{t('sectionB74.colEquation')}</th>
                    <th>{t('sectionB74.colParameters')}</th>
                    <th>{t('sectionB74.colResult')}</th>
                    <th style={{ width: 160, textAlign: 'center' }}>{t('sectionB74.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {equations.map((entry, i) => (
                    <tr key={entry.id}>
                      <td style={{ textAlign: 'center' }}>{i + 1}</td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 6px', background: '#e8f4ff', borderRadius: 4, color: '#1a5276', whiteSpace: 'nowrap' }}>
                          {getEquationFromLabel(entry.equation_from)}
                        </span>
                      </td>
                      <td><div style={{ fontWeight: 500 }}>{entry.name || '—'}</div></td>
                      <td>
                        {entry.equation ? (
                          <div style={{ maxWidth: 280, overflow: 'auto' }}>
                            {(() => { try { return <BlockMath math={entry.equation} />; } catch { return <code>{entry.equation}</code>; } })()}
                          </div>
                        ) : '—'}
                      </td>
                      <td>
                        {(entry.method_choices || []).length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                            {entry.method_choices.map((p, pi) => (
                              <li key={pi}>
                                <code>{p.symbol}</code>
                                {p.annual_value != null && (
                                  <span> = {fmtNum(p.annual_value)}{' '}
                                    {p.unit && (
                                      (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                                    )}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : '—'}
                      </td>
                      <td>
                        {entry.calculation_result?.value != null ? (
                          <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                            <span>{fmtNum(entry.calculation_result.value)}</span>
                            {entry.calculation_result.unit && (
                              <span style={{ fontSize: 12 }}>
                                {(() => { try { return <InlineMath math={entry.calculation_result.unit} />; } catch { return entry.calculation_result.unit; } })()}
                              </span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <button onClick={() => openEdit(entry)}
                            style={{ padding: '6px 16px', background: '#fff', border: '1px solid #4A90E2', color: '#4A90E2', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            {t('common.edit')}
                          </button>
                          {canEdit && (
                            <button onClick={() => handleDelete(entry.id)}
                              style={{ padding: '6px 16px', background: '#fff', border: '1px solid #e53935', color: '#e53935', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                              {t('common.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '48px', textAlign: 'center', color: '#aaa', fontSize: 14 }}>
              {t('sectionB74.noEquations')}
            </div>
          )}

          {/* 年度預估減量試算區塊 */}
          {selectedProjectId && (
            <SpreadsheetSection
              projectId={selectedProjectId}
              sectionCComplete={data?.section_c_complete ?? false}
              creditingPeriodDuration={data?.crediting_period_duration ?? null}
              isZh={isZh}
            />
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && selectedProjectId && (
        <EquationModal
          projectId={selectedProjectId}
          sectionId={data?.id ?? 0}
          initialEntry={modalEntry ?? null}
          isZh={isZh}
          canEdit={canEdit}
          paraTypeCodes={paraTypeCodes}
          equationFromCodes={equationFromCodes}
          onClose={closeModal}
          onDataChange={handleDataChange}
        />
      )}
    </div>
  );
};

export default SectionB74Page;
